/* Business bundles — collection grid.
 * AJAX filters / sort / pagination (re-renders the section via ?section_id)
 * plus the mobile filter & sort drawer and desktop dropdown behaviour.
 */
(() => {
  if (customElements.get('bbc-collection')) return;

  class BbcCollection extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.drawer = this.querySelector('[data-bbc-drawer]');
      this.controller = null;
      this.priceTimer = null;
      this.lastFocused = null;

      this.handleChange = this.handleChange.bind(this);
      this.handleInput = this.handleInput.bind(this);
      this.handleClick = this.handleClick.bind(this);
      this.handleToggle = this.handleToggle.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);
      this.handleDocumentClick = this.handleDocumentClick.bind(this);
      this.handlePopState = this.handlePopState.bind(this);

      this.addEventListener('change', this.handleChange);
      this.addEventListener('input', this.handleInput);
      this.addEventListener('click', this.handleClick);
      this.addEventListener('toggle', this.handleToggle, true);
      document.addEventListener('keydown', this.handleKeydown);
      document.addEventListener('click', this.handleDocumentClick);
      window.addEventListener('popstate', this.handlePopState);
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this.handleKeydown);
      document.removeEventListener('click', this.handleDocumentClick);
      window.removeEventListener('popstate', this.handlePopState);
      document.body.classList.remove('bbc-scroll-lock');
    }

    /* ---------- Events ---------- */

    handleChange(event) {
      const input = event.target;
      if (input.type === 'number') return; // price inputs are debounced on input
      const form = input.closest('form[data-bbc-form]');
      if (form) this.submitForm(form);
    }

    handleInput(event) {
      const input = event.target;
      if (input.type !== 'number') return;
      const form = input.closest('form[data-bbc-form]');
      if (!form) return;
      clearTimeout(this.priceTimer);
      this.priceTimer = setTimeout(() => this.submitForm(form), 500);
    }

    handleClick(event) {
      const opener = event.target.closest('[data-bbc-open]');
      if (opener) {
        event.preventDefault();
        this.openDrawer(opener.dataset.bbcOpen);
        return;
      }

      if (event.target.closest('[data-bbc-close]')) {
        event.preventDefault();
        this.closeDrawer();
        return;
      }

      const link = event.target.closest('a[data-bbc-link], a[data-bbc-page]');
      if (!link) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
      event.preventDefault();
      this.load(link.href, { scroll: link.hasAttribute('data-bbc-page') });
    }

    handleToggle(event) {
      const details = event.target;
      if (!(details instanceof HTMLDetailsElement)) return;
      if (!details.hasAttribute('data-bbc-dropdown') || !details.open) return;
      this.querySelectorAll('details[data-bbc-dropdown][open]').forEach((other) => {
        if (other !== details) other.open = false;
      });
    }

    handleDocumentClick(event) {
      this.querySelectorAll('details[data-bbc-dropdown][open]').forEach((details) => {
        if (!details.contains(event.target)) details.open = false;
      });
    }

    handleKeydown(event) {
      if (event.key !== 'Escape') return;
      const openDropdowns = this.querySelectorAll('details[data-bbc-dropdown][open]');
      openDropdowns.forEach((details) => {
        details.open = false;
        const summary = details.querySelector('summary');
        if (summary) summary.focus();
      });
      if (this.drawer && !this.drawer.hidden) this.closeDrawer();
    }

    handlePopState() {
      this.load(window.location.href, { push: false });
    }

    /* ---------- Form → URL ---------- */

    submitForm(form) {
      const params = new URLSearchParams();
      const defaultSort = this.dataset.defaultSort || '';
      new FormData(form).forEach((value, key) => {
        const text = String(value).trim();
        if (text === '') return;
        if (key === 'sort_by' && text === defaultSort) return;
        params.append(key, text);
      });
      const query = params.toString();
      this.load(`${window.location.pathname}${query ? `?${query}` : ''}`);
    }

    /* ---------- Fetch + render ---------- */

    async load(url, options = {}) {
      const { push = true, scroll = false } = options;
      const target = new URL(url, window.location.origin);

      // Keep an alternate-template preview (?view=…) alive across AJAX updates.
      const view = new URLSearchParams(window.location.search).get('view');
      if (view && !target.searchParams.has('view')) target.searchParams.set('view', view);

      const request = new URL(target.toString());
      request.searchParams.set('section_id', this.sectionId);

      if (this.controller) this.controller.abort();
      this.controller = new AbortController();
      this.classList.add('is-loading');
      this.setAttribute('aria-busy', 'true');

      try {
        const response = await fetch(request.toString(), { signal: this.controller.signal });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const fresh = doc.querySelector('bbc-collection');
        if (!fresh) throw new Error('Section markup missing');

        this.render(fresh);
        if (push) window.history.pushState({ bbc: this.sectionId }, '', `${target.pathname}${target.search}`);
        if (scroll) this.scrollToTop();
      } catch (error) {
        if (error.name === 'AbortError') return;
        window.location.assign(target.toString());
        return;
      }

      this.classList.remove('is-loading');
      this.removeAttribute('aria-busy');
    }

    render(fresh) {
      this.swap(fresh, '[data-bbc-results]');
      this.swap(fresh, '[data-bbc-active]');
      this.copyText(fresh, '[data-bbc-count]');
      this.copyText(fresh, '[data-bbc-filter-count]');
      this.copyText(fresh, '[data-bbc-drawer-apply]');

      const active = document.activeElement;

      this.querySelectorAll('[data-bbc-facet]').forEach((node) => {
        if (active && node.contains(active)) return; // keep the control the user is typing in
        const next = this.matchScoped(fresh, node, 'data-bbc-facet');
        if (next) node.innerHTML = next.innerHTML;
      });

      this.querySelectorAll('[data-bbc-facet-label]').forEach((node) => {
        const next = this.matchScoped(fresh, node, 'data-bbc-facet-label');
        if (next) node.textContent = next.textContent;
      });
    }

    matchScoped(fresh, node, attribute) {
      const scope = node.closest('[data-bbc-form]');
      if (!scope) return null;
      const value = node.getAttribute(attribute).replace(/"/g, '\\"');
      return fresh.querySelector(`[data-bbc-form="${scope.dataset.bbcForm}"] [${attribute}="${value}"]`);
    }

    swap(fresh, selector) {
      const next = fresh.querySelector(selector);
      const current = this.querySelector(selector);
      if (next && current) current.replaceWith(next);
    }

    copyText(fresh, selector) {
      const next = fresh.querySelector(selector);
      if (!next) return;
      this.querySelectorAll(selector).forEach((node) => {
        node.textContent = next.textContent;
      });
    }

    scrollToTop() {
      const anchor = this.querySelector('[data-bbc-scroll-target]') || this;
      const offset = anchor.getBoundingClientRect().top + window.scrollY - 96;
      window.scrollTo({ top: Math.max(offset, 0), behavior: 'smooth' });
    }

    /* ---------- Mobile drawer ---------- */

    openDrawer(target) {
      if (!this.drawer) return;
      this.lastFocused = document.activeElement;
      this.drawer.hidden = false;
      document.body.classList.add('bbc-scroll-lock');
      requestAnimationFrame(() => this.drawer.classList.add('is-open'));

      const group = this.drawer.querySelector(`[data-bbc-group="${target}"]`);
      const body = this.drawer.querySelector('[data-bbc-drawer-body]');
      if (group) {
        group.open = true;
        if (body) body.scrollTop = group.offsetTop;
      }

      const closeButton = this.drawer.querySelector('[data-bbc-close-button]');
      if (closeButton) closeButton.focus({ preventScroll: true });
    }

    closeDrawer() {
      if (!this.drawer || this.drawer.hidden) return;
      const drawer = this.drawer;
      drawer.classList.remove('is-open');
      document.body.classList.remove('bbc-scroll-lock');
      setTimeout(() => {
        if (!drawer.classList.contains('is-open')) drawer.hidden = true;
      }, 300);
      if (this.lastFocused && typeof this.lastFocused.focus === 'function') {
        this.lastFocused.focus({ preventScroll: true });
      }
    }
  }

  customElements.define('bbc-collection', BbcCollection);
})();
